import { useBillingServiceStore } from '@billing/AppProvider'
import { ContainerJourneyDto } from '@billing/app/api'
import ReportStep from '@billing/app/models/report-step.enum'
import { Box, CircularProgress, Stack } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  EmptyIndicator,
  TomTable,
  tomTableColumnFilterPresets,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import {
  exportToCsv,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { useEffect, useMemo } from 'react'
import { CustomerBillingReportFilter } from '../components/customerBillingReport/CustomerBillingReportFilter'
import { ReportNavigationButtonContainer } from './ReportNavigationButtonContainer'

export const CustomerBillingContainer = observer(() => {
  const { reportsUIStore, customerBillingUIStore } = useBillingServiceStore()
  const { t } = useTranslate()

  const dataExportConfig = useMemo<IDataExportConfig<any>>(
    () => ({
      referenceNumber: { header: t('referenceNumber', 'Reference number'), type: 'string' },
      containerNumber: { header: t('containerNumber', 'Container number'), type: 'string' },
      teu: { header: t('teu', 'TEU'), type: 'number' },
      storageDaysEmpty: { header: t('storageDaysEmpty', 'Storage days empty'), type: 'number' },
      storageDaysFull: { header: t('storageDaysFull', 'Storage days full'), type: 'number' },
      totalStorageDays: { header: t('totalStorageDays', 'Total storage days'), type: 'number' },
      totalPicks: { header: t('totalPicks', 'Total picks'), type: 'number' },
      // Inbound
      in_HandlingDirection: {
        header: `In ${t('handlingDirection', 'Handling direction')}`,
        type: 'string',
      },
      in_Date: { header: `In ${t('date', 'Date')}`, type: 'date' },
      in_CarrierName: { header: `In ${t('carrierName', 'Carrier name')}`, type: 'string' },
      in_CarrierType: { header: `In ${t('carrierType', 'Carrier type')}`, type: 'string' },
      in_GrossWeight: { header: `In ${t('grossWeight', 'Gross weight')}`, type: 'number' },
      in_IsDangerous: { header: `In ${t('isDangerous', 'Is dangerous')}`, type: 'string' },
      in_IsEmpty: { header: `In ${t('isEmpty', 'Is empty')}`, type: 'string' },
      // Outbound
      out_HandlingDirection: {
        header: `Out ${t('handlingDirection', 'Handling direction')}`,
        type: 'string',
      },
      out_Date: { header: `Out ${t('date', 'Date')}`, type: 'date' },
      out_CarrierName: { header: `Out ${t('carrierName', 'Carrier name')}`, type: 'string' },
      out_CarrierType: { header: `Out ${t('carrierType', 'Carrier type')}`, type: 'string' },
      out_GrossWeight: { header: `Out ${t('grossWeight', 'Gross weight')}`, type: 'number' },
      out_IsDangerous: { header: `Out ${t('isDangerous', 'Is dangerous')}`, type: 'string' },
      out_IsEmpty: { header: `Out ${t('isEmpty', 'Is empty')}`, type: 'string' },
    }),
    [t],
  )

  const dataExport = {
    title: t('customerBillingContainersReport', 'Customer Billing Containers Report'),
    enableDataExport: true,
    dataExportConfig,
  }

  const columns = useMemo<MRT_ColumnDef<ContainerJourneyDto>[]>(
    () => [
      {
        id: 'referenceNumber',
        header: t('referenceNumber', 'Reference number'),
        accessorKey: 'referenceNumber',
        size: 200,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'containerNumber',
        header: t('containerNumber', 'Container number'),
        accessorKey: 'containerNumber',
        size: 200,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'teu',
        header: t('teu', 'TEU'),
        accessorKey: 'teu',
        size: 80,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'storageDaysEmpty',
        header: t('storageDaysEmpty', 'Storage days empty'),
        accessorKey: 'storageDaysEmpty',
        size: 200,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'storageDaysFull',
        header: t('storageDaysFull', 'Storage days full'),
        accessorKey: 'storageDaysFull',
        size: 200,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'totalStorageDays',
        header: t('totalStorageDays', 'Total storage days'),
        accessorKey: 'totalStorageDays',
        size: 200,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'totalPicks',
        header: t('totalPicks', 'Total picks'),
        accessorKey: 'totalPicks',
        size: 80,
        ...tomTableColumnFilterPresets.numericOptional(),
      },

      // INBOUND (orderDetails[0])
      {
        id: 'in_HandlingDirection',
        header: `In ${t('handlingDirection', 'Handling direction')}`,
        size: 250,
        accessorFn: row => row.orderDetails[0]?.handlingDirection,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'in_Date',
        header: `In ${t('date', 'Date')}`,
        accessorFn: row => row.orderDetails[0]?.date,
        Cell: ({ cell }) =>
          cell.getValue<string>() ? dayjs(cell.getValue<string>()).format('L LT') : EmptyIndicator,
        ...tomTableColumnFilterPresets.dateOptional(),
      },
      {
        id: 'in_CarrierName',
        header: `In ${t('carrierName', 'Carrier name')}`,
        accessorFn: row => row.orderDetails[0]?.carrierName,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'in_CarrierType',
        header: `In ${t('carrierType', 'Carrier type')}`,
        accessorFn: row => row.orderDetails[0]?.carrierType,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'in_GrossWeight',
        header: `In ${t('grossWeight', 'Gross weight')}`,
        accessorFn: row => row.orderDetails[0]?.grossWeight,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'in_IsDangerous',
        header: `In ${t('isDangerous', 'Is dangerous')}`,
        accessorFn: row => (row.orderDetails[0]?.isDangerous ? t('yes', 'Yes') : t('no', 'No')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'in_IsEmpty',
        header: `In ${t('isEmpty', 'Is empty')}`,
        accessorFn: row => (row.orderDetails[0]?.isEmpty ? t('yes', 'Yes') : t('no', 'No')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },

      // OUTBOUND (orderDetails[1])
      {
        id: 'out_HandlingDirection',
        header: `Out ${t('handlingDirection', 'Handling direction')}`,
        size: 250,
        accessorFn: row => row.orderDetails[1]?.handlingDirection,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'out_Date',
        header: `Out ${t('date', 'Date')}`,
        accessorFn: row => row.orderDetails[1]?.date,
        Cell: ({ cell }) =>
          cell.getValue<string>() ? dayjs(cell.getValue<string>()).format('L LT') : EmptyIndicator,
        ...tomTableColumnFilterPresets.dateOptional(),
      },
      {
        id: 'out_CarrierName',
        header: `Out ${t('carrierName', 'Carrier name')}`,
        accessorFn: row => row.orderDetails[1]?.carrierName,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'out_CarrierType',
        header: `Out ${t('carrierType', 'Carrier type')}`,
        accessorFn: row => row.orderDetails[1]?.carrierType,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'out_GrossWeight',
        header: `Out ${t('grossWeight', 'Gross weight')}`,
        accessorFn: row => row.orderDetails[1]?.grossWeight,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'out_IsDangerous',
        header: `Out ${t('isDangerous', 'Is dangerous')}`,
        accessorFn: row => (row.orderDetails[1]?.isDangerous ? t('yes', 'Yes') : t('no', 'No')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'out_IsEmpty',
        header: `Out ${t('isEmpty', 'Is empty')}`,
        accessorFn: row => (row.orderDetails[1]?.isEmpty ? t('yes', 'Yes') : t('no', 'No')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t],
  )

  const defaultOptions = useDefaultMRTOptions<ContainerJourneyDto>(undefined, dataExport)

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'billing/customer-billing',
  )

  const table = useTomTable(
    {
      columns,
      data: customerBillingUIStore.billingData,
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      enableRowActions: false,
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: ['referenceNumber', 'containerNumber'],
        },
      },
      muiTableContainerProps: {
        sx: {
          minHeight: '450px',
        },
      },
    },
    undefined,
    dataExport,
    { columns, savedPreferences, onPreferencesChange },
  )

  const handleDownload = useMemo(
    () => () => {
      const rows = table.getPrePaginationRowModel().rows
      const dataToExport = rows.map(row => {
        const item = row.original
        const inb = item.orderDetails[0]
        const out = item.orderDetails[1]

        return {
          ...item,
          // Inbound
          in_HandlingDirection: inb?.handlingDirection,
          in_Date: inb?.date,
          in_CarrierName: inb?.carrierName,
          in_CarrierType: inb?.carrierType,
          in_GrossWeight: inb?.grossWeight,
          in_IsDangerous: inb?.isDangerous ? t('yes') : t('no'),
          in_IsEmpty: inb?.isEmpty ? t('empty') : t('full'),
          // Outbound
          out_HandlingDirection: out?.handlingDirection,
          out_Date: out?.date,
          out_CarrierName: out?.carrierName,
          out_CarrierType: out?.carrierType,
          out_GrossWeight: out?.grossWeight,
          out_IsDangerous: out?.isDangerous ? t('yes') : t('no'),
          out_IsEmpty: out?.isEmpty ? t('empty') : t('full'),
        }
      })

      exportToCsv(
        t('billingReport', 'Customer Billing Report'),
        dataToExport,
        dataExportConfig,
        `Billing_${dayjs().format('YYYYMMDD')}.csv`,
      )
    },
    [table, t, dataExportConfig],
  )

  useEffect(() => {
    if (reportsUIStore.currentStep === ReportStep.Report) {
      customerBillingUIStore.fetchBillingData()
    }
  }, [reportsUIStore.currentStep, customerBillingUIStore])

  return (
    <>
      {reportsUIStore.currentStep === ReportStep.Filter && <CustomerBillingReportFilter />}

      {reportsUIStore.currentStep === ReportStep.Report && (
        <Box sx={{ width: '100%', mt: 1 }}>
          {customerBillingUIStore.isLoading ? (
            <Stack alignItems='center' py={5}>
              <CircularProgress />
            </Stack>
          ) : (
            <TomTable table={table} />
          )}
        </Box>
      )}

      <ReportNavigationButtonContainer
        disableNext={
          !customerBillingUIStore.customerId ||
          (!customerBillingUIStore.from && !customerBillingUIStore.to)
        }
        downloadAction={() => handleDownload()}
        finishAction={() => {
          customerBillingUIStore.resetData()
          reportsUIStore.resetStepper()
        }}
      />
    </>
  )
})
